import { Injectable, inject } from "@angular/core";
import { StatusParams } from "../../msst/models/msst";
import { BehaviorSubject } from "rxjs";
import { HttpClient, HttpParams } from "@angular/common/http";
import { environment } from "../../../environments/environment";
import { APIResponse } from "../../models/APIResponse";
import { ComponentMaster, ExcelProcessError, ExcelProcessSuccess, ExcelRow, FileInputLike, FormLike, InitialDatasetsResult, RecombinationQCCalculation, RecombinationQCFinalReport, RecombinationQCSaveModel, RecombinationRecombinedSeparatorModel, RecombinationResult, RecombinedOilModel, SeparatorGasModel, SeparatorOilModel, TabStateSnapshot } from "../models/recombination-qc";
import { COMPONENT_CONSTANTS, PARAFFIN_CONSTANTS as PC } from "../../common/constants";
import { API_ENDPOINTS as AE, RECOMBINATION_CONSTANTS, REGEX_CONSTANTS } from "../../common/experiment-constants";
import { MatTabGroup } from "@angular/material/tabs";
import { NUMBERS_CONSTANTS } from "../../common/numbers-constants";
import { apiV1 } from "../../common/texts-constants";
import { Validators } from "@angular/forms";

@Injectable({
  providedIn: 'root'
})
export class RecombinationQCService {
  private http = inject(HttpClient);

  public statusChange$ = new BehaviorSubject<StatusParams>({ statusId: 0, statusName: '' });
  private apiBaseUrl = environment.apiUrl+apiV1;

  UpdateStatus(status: StatusParams) {
    this.statusChange$.next(status);
  }

  GetRecombinationQC(labTestRepeatId: number) {
    const params = new HttpParams().set(PC.testRepeatId, labTestRepeatId);
    return this.http.get<APIResponse<RecombinationQCSaveModel>>(`${this.apiBaseUrl}${AE.GETRECOMBINATIONQC}`, { params });
  }

  SaveRecombinationQC(data: RecombinationQCSaveModel) {
    return this.http.post<APIResponse<RecombinationQCSaveModel>>(`${this.apiBaseUrl}${AE.SAVERECOMBINATIONQC}`, data);
  }

  UpdateRecombinationQC(data: RecombinationQCSaveModel) {
    return this.http.post<APIResponse<RecombinationQCSaveModel>>(`${this.apiBaseUrl}${AE.UPDATERECOMBINATIONQC}`, data);
  }

  PartialRecombinationQC(data: RecombinationQCSaveModel) {
    return this.http.post<APIResponse<RecombinationQCSaveModel>>(`${this.apiBaseUrl}${AE.PARTIALSAVERECOMBINATIONQC}`, data);
  }

  GetFinalReportRecombinationQC(testRepeatId: number) {
    const params = new HttpParams().set(PC.testRepeatId, testRepeatId)
    return this.http.get<APIResponse<RecombinationQCFinalReport>>(`${this.apiBaseUrl}${AE.GETFINALREPORTRECOMBINATIONQC}`, { params });
  }


  ProcessCalculationChange(data: RecombinationQCCalculation) {
    return {
      recombinedOil: data.recombinationRecombinedSeparatorModel || [],
      recombinationMathFluid: data.recombinationMathematicalFluidModel || [],
      recombGORCheckModel: data.recombinationRecombGORCheckModel
    };
  }

  GoToPreviousTab(tabGroup: MatTabGroup, oldTab: number) {
    if (tabGroup.selectedIndex === NUMBERS_CONSTANTS.zero) {
      return { selectedIndex: null, updatedOldTab: oldTab - NUMBERS_CONSTANTS.one };
    }
    return { selectedIndex: this.GetPreviousIndex(tabGroup), updatedOldTab: oldTab };
  }

  private GetPreviousIndex(tabGroup: MatTabGroup) {
    if (!tabGroup || !(tabGroup instanceof MatTabGroup)) return null;
    const tabCount = tabGroup._tabs.length;
    const selectedIndex = tabGroup.selectedIndex || NUMBERS_CONSTANTS.zero;
    if (selectedIndex !== NUMBERS_CONSTANTS.zero) {
      return (selectedIndex - NUMBERS_CONSTANTS.one) % tabCount;
    }
    return selectedIndex;
  }

  GoToNextTab(tabGroup: MatTabGroup, oldTab: number) {
    if (tabGroup.selectedIndex === NUMBERS_CONSTANTS.three) {
      return { selectedIndex: null, updatedOldTab: oldTab + NUMBERS_CONSTANTS.one };
    }
    return { selectedIndex: this.GetNextIndex(tabGroup), updatedOldTab: oldTab };
  }

  private GetNextIndex(tabGroup: MatTabGroup) {
    if (!tabGroup || !(tabGroup instanceof MatTabGroup)) return null;
    const tabCount = tabGroup._tabs.length;
    const selectedIndex = tabGroup.selectedIndex || NUMBERS_CONSTANTS.zero;
    return (selectedIndex + NUMBERS_CONSTANTS.one) % tabCount;
  }

  NormalizeSnapshotValue(value: unknown): unknown {
    if (value === undefined || value === PC.emptyString) {
      return null;
    }
    if (Array.isArray(value)) {
      return value.map((item) => this.NormalizeSnapshotValue(item));
    }
    if (value && typeof value === 'object') {
      return Object.entries(value as Record<string, unknown>).reduce<Record<string, unknown>>((acc, [key, entryValue]) => {
        acc[key] = this.NormalizeSnapshotValue(entryValue);
        return acc;
      }, {});
    }
    return value;
  }

  BuildTabStateSnapshot(
    tabIndex: number,
    form: FormLike,
    recombinedOilData: unknown[],
    separatorOilData: unknown[],
    separatorGasData: unknown[],
    notes: string
  ): string {
    switch (tabIndex) {
      case NUMBERS_CONSTANTS.zero:
        return JSON.stringify(this.NormalizeSnapshotValue(form?.getRawValue?.()));
      case NUMBERS_CONSTANTS.one:
        return JSON.stringify(this.NormalizeSnapshotValue(recombinedOilData));
      case NUMBERS_CONSTANTS.two:
        return JSON.stringify({
          separatorOilData: this.NormalizeSnapshotValue(separatorOilData),
          separatorGasData: this.NormalizeSnapshotValue(separatorGasData)
        });
      case NUMBERS_CONSTANTS.three:
        return JSON.stringify(this.NormalizeSnapshotValue(notes));
      default:
        return PC.emptyString;
    }
  }

  CaptureInitialTabStates(
    form: FormLike,
    recombinedOilData: unknown[],
    separatorOilData: unknown[],
    separatorGasData: unknown[],
    notes: string
  ): TabStateSnapshot {
    return {
      [NUMBERS_CONSTANTS.zero]: this.BuildTabStateSnapshot(NUMBERS_CONSTANTS.zero, form, recombinedOilData, separatorOilData, separatorGasData, notes),
      [NUMBERS_CONSTANTS.one]: this.BuildTabStateSnapshot(NUMBERS_CONSTANTS.one, form, recombinedOilData, separatorOilData, separatorGasData, notes),
      [NUMBERS_CONSTANTS.two]: this.BuildTabStateSnapshot(NUMBERS_CONSTANTS.two, form, recombinedOilData, separatorOilData, separatorGasData, notes),
      [NUMBERS_CONSTANTS.three]: this.BuildTabStateSnapshot(NUMBERS_CONSTANTS.three, form, recombinedOilData, separatorOilData, separatorGasData, notes)
    };
  }

  UpdateUnsavedChangesFlag(
    tabIndex: number,
    initialTabStates: TabStateSnapshot,
    form: FormLike,
    recombinedOilData: unknown[],
    separatorOilData: unknown[],
    separatorGasData: unknown[],
    notes: string
  ): boolean {
    return this.BuildTabStateSnapshot(tabIndex, form, recombinedOilData, separatorOilData, separatorGasData, notes)
      !== (initialTabStates[tabIndex] ?? PC.emptyString);
  }

  HasAnyUnsavedChanges(
    initialTabStates: TabStateSnapshot,
    form: FormLike,
    recombinedOilData: unknown[],
    separatorOilData: unknown[],
    separatorGasData: unknown[],
    notes: string
  ): boolean {
    const trackedTabs = [
      NUMBERS_CONSTANTS.zero,
      NUMBERS_CONSTANTS.one,
      NUMBERS_CONSTANTS.two,
      NUMBERS_CONSTANTS.three
    ];

    return trackedTabs.some(tab =>
      this.BuildTabStateSnapshot(tab, form, recombinedOilData, separatorOilData, separatorGasData, notes)
      !== (initialTabStates[tab] ?? PC.emptyString)
    );
  }

  HasInputTabErrors(form: FormLike): boolean {
    const fields = [
      { control: RECOMBINATION_CONSTANTS.targetSepGOR },
      { control: RECOMBINATION_CONSTANTS.sepOilDensity },
      { control: RECOMBINATION_CONSTANTS.sepOilMW },
      { control: RECOMBINATION_CONSTANTS.recombOilCThirtySixPlusMW },
      { control: RECOMBINATION_CONSTANTS.recombFlashOilDensity },
      { control: RECOMBINATION_CONSTANTS.recombMeasGOR }
    ];

    return fields.some(field => {
      const value = form.controls[field.control]?.value;
      return this.IsMissingValue(value) || this.HasInvalidNumberFormat(value);
    });
  }

  IsMissingValue(value: unknown): boolean {
    return value === null || value === undefined || value === PC.emptyString;
  }

  HasInvalidNumberFormat(value: unknown): boolean {
    if (this.IsMissingValue(value)) return false;
    return !REGEX_CONSTANTS.NUMBER_WITH_OPTIONAL_DECIMAL.test(String(value));
  }

  ParseNumber(value: unknown): number | null {
    return value === null || value === '' || value === undefined ? null : Number(value);
  }

  BuildInitialDatasetsFromComponents(components: ComponentMaster[]): InitialDatasetsResult {
    const sourceComponents = Array.isArray(components) ? components : [];
    const c36Component = sourceComponents.find((data) => data.component === RECOMBINATION_CONSTANTS.cThirtySix);
    const componentIdc36 = c36Component?.componentId ?? null;

    const c12Component = sourceComponents.find((data) => data.component === RECOMBINATION_CONSTANTS.cTwelve);
    const componentC12Id = c12Component?.componentId ?? NUMBERS_CONSTANTS.zero;

    const c2Component = sourceComponents.find((data) => data.component === RECOMBINATION_CONSTANTS.cTwo);
    const componentC2Id = c2Component?.componentId ?? NUMBERS_CONSTANTS.zero;

    const normalizedComponents = sourceComponents.map((data) => {
      const normalized = { ...data };
      if (normalized.component === RECOMBINATION_CONSTANTS.cThirtySix) {
        normalized.component = normalized.component.split('/')[NUMBERS_CONSTANTS.one] ?? normalized.component;
        normalized.componentName = normalized.componentName?.split(' ')[NUMBERS_CONSTANTS.zero] ?? normalized.componentName;
      }
      return normalized;
    });

    const componentMaster = normalizedComponents.filter((data) => {
      return componentIdc36 === null || data.componentId <= componentIdc36;
    });

    const includeComponent = (componentId: number): boolean => componentIdc36 === null || componentId <= componentIdc36;

    const recombinedOilData = normalizedComponents
      .filter((data) => includeComponent(data.componentId))
      .map((data) => ({
        recombinedOilId: NUMBERS_CONSTANTS.zero,
        componentId: data.componentId,
        component: data.component,
        componentName: data.componentName,
        roFlashedLiquidWeightPercentage: null,
        roFlashedLiquidMolePercentage: null,
        roFlashedGasWeightPercentage: null,
        roFlashedGasMolePercentage: null,
        roWholeFluidWeightPercentage: null,
        roWholeFluidMolePercentage: null,
      }));

    const separatorOilData = normalizedComponents
      .filter((data) => includeComponent(data.componentId))
      .map((data) => ({
        separatorOilId: NUMBERS_CONSTANTS.zero,
        componentId: data.componentId,
        component: data.component,
        componentName: data.componentName,
        soFlashedLiquidWeightPercentage: null,
        soFlashedLiquidMolePercentage: null,
        soFlashedGasWeightPercentage: null,
        soFlashedGasMolePercentage: null,
        soWholeFluidWeightPercentage: null,
        soWholeFluidMolePercentage: null,
      }));

    const separatorGasData = normalizedComponents
      .filter((data) => includeComponent(data.componentId))
      .map((data) => ({
        separatorGasId: NUMBERS_CONSTANTS.zero,
        componentId: data.componentId,
        component: data.component,
        componentName: data.componentName,
        sgWeightPercentage: null,
        sgMolePercentage: null,
      }));

    return {
      componentMaster,
      componentC2Id,
      componentC12Id,
      recombinedOilData,
      separatorOilData,
      separatorGasData
    };
  }

  ApplyPastedGridData(
    pastedData: string,
    rowIndex: number,
    column: string,
    data: (RecombinedOilModel | SeparatorOilModel | SeparatorGasModel)[],
    columns: string[]
  ): (RecombinedOilModel | SeparatorOilModel | SeparatorGasModel)[] {
    const normalized = pastedData.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    const rows = normalized.split('\n');

    if (rows.length && rows[rows.length - NUMBERS_CONSTANTS.one] === PC.emptyString) {
      rows.pop();
    }

    const parsedData = rows.map((row) => row.split('\t'));
    const datasourceData = [...data];
    const startColIndex = columns.indexOf(column);

    parsedData.forEach((cols, rowOffset) => {
      const targetRow = rowIndex + rowOffset;

      cols.forEach((value, colOffset) => {
        const colName = columns[startColIndex + colOffset];
        const targetRecord = datasourceData[targetRow] as unknown as Record<string, unknown> | undefined;
        if (colName && targetRecord) {
          targetRecord[colName] = value?.trim() || null;
        }
      });
    });

    return datasourceData;
  }

  MergeModels(recombinedOilList: RecombinedOilModel[], separatorOilList: SeparatorOilModel[], separatorGasList: SeparatorGasModel[]): RecombinationRecombinedSeparatorModel[] {
    const mergedMap = new Map<number, RecombinationRecombinedSeparatorModel>();

    const getOrCreateModel = (componentId: number, componentName?: string): RecombinationRecombinedSeparatorModel => {
      if (!mergedMap.has(componentId)) {
        mergedMap.set(componentId, {
          recombinationRecombinedSeparatorId: NUMBERS_CONSTANTS.zero,
          componentId,
          componentName: componentName || PC.emptyString,
        });
      }
      return mergedMap.get(componentId)!;
    };

    for (const ro of recombinedOilList) {
      if (ro.componentId !== null && ro.componentId !== undefined && ro.component?.toLowerCase() !== RECOMBINATION_CONSTANTS.totals) {
        const model = getOrCreateModel(ro.componentId, ro.component);
        Object.assign(model, {
          recombinationRecombinedSeparatorId: ro.recombinedOilId,
          roFlashedLiquidWeightPercentage: ro.roFlashedLiquidWeightPercentage,
          roFlashedLiquidMolePercentage: ro.roFlashedLiquidMolePercentage,
          roFlashedGasWeightPercentage: ro.roFlashedGasWeightPercentage,
          roFlashedGasMolePercentage: ro.roFlashedGasMolePercentage,
          roWholeFluidWeightPercentage: ro.roWholeFluidWeightPercentage,
          roWholeFluidMolePercentage: ro.roWholeFluidMolePercentage,
        });
      }
    }

    for (const so of separatorOilList) {
      if (so.componentId !== null && so.componentId !== undefined && so.component?.toLowerCase() !== RECOMBINATION_CONSTANTS.totals) {
        const model = getOrCreateModel(so.componentId, so.component);
        Object.assign(model, {
          recombinationRecombinedSeparatorId: so.separatorOilId,
          soFlashedLiquidWeightPercentage: so.soFlashedLiquidWeightPercentage,
          soFlashedLiquidMolePercentage: so.soFlashedLiquidMolePercentage,
          soFlashedGasWeightPercentage: so.soFlashedGasWeightPercentage,
          soFlashedGasMolePercentage: so.soFlashedGasMolePercentage,
          soWholeFluidWeightPercentage: so.soWholeFluidWeightPercentage,
          soWholeFluidMolePercentage: so.soWholeFluidMolePercentage,
        });
      }
    }

    for (const sg of separatorGasList) {
      if (sg.componentId !== null && sg.componentId !== undefined && sg.component?.toLowerCase() !== RECOMBINATION_CONSTANTS.totals) {
        const model = getOrCreateModel(sg.componentId, sg.component);
        Object.assign(model, {
          recombinationRecombinedSeparatorId: sg.separatorGasId,
          sgWeightPercentage: sg.sgWeightPercentage,
          sgMolePercentage: sg.sgMolePercentage,
        });
      }
    }

    return Array.from(mergedMap.values());
  }

  SplitMergedModels(
    mergedList: RecombinationRecombinedSeparatorModel[],
    componentMaster: ComponentMaster[]
  ): {
    recombinedOilList: RecombinedOilModel[];
    separatorOilList: SeparatorOilModel[];
    separatorGasList: SeparatorGasModel[];
  } {
    const recombinedOilList: RecombinedOilModel[] = [];
    const separatorOilList: SeparatorOilModel[] = [];
    const separatorGasList: SeparatorGasModel[] = [];

    const componentMap = new Map<number, ComponentMaster>();
    (componentMaster || []).forEach(comp => {
      componentMap.set(comp.componentId, comp);
    });

    for (const item of (mergedList || [])) {
      const comp = componentMap.get(item.componentId);
      const componentName = comp?.componentName;
      const component = comp?.component || item.componentName;

      recombinedOilList.push({
        recombinedOilId: item.recombinationRecombinedSeparatorId,
        componentId: item.componentId,
        componentName,
        component,
        roFlashedLiquidWeightPercentage: item.roFlashedLiquidWeightPercentage,
        roFlashedLiquidMolePercentage: item.roFlashedLiquidMolePercentage,
        roFlashedGasWeightPercentage: item.roFlashedGasWeightPercentage,
        roFlashedGasMolePercentage: item.roFlashedGasMolePercentage,
        roWholeFluidWeightPercentage: item.roWholeFluidWeightPercentage,
        roWholeFluidMolePercentage: item.roWholeFluidMolePercentage
      });

      separatorOilList.push({
        separatorOilId: item.recombinationRecombinedSeparatorId,
        componentId: item.componentId,
        componentName,
        component,
        soFlashedLiquidWeightPercentage: item.soFlashedLiquidWeightPercentage,
        soFlashedLiquidMolePercentage: item.soFlashedLiquidMolePercentage,
        soFlashedGasWeightPercentage: item.soFlashedGasWeightPercentage,
        soFlashedGasMolePercentage: item.soFlashedGasMolePercentage,
        soWholeFluidWeightPercentage: item.soWholeFluidWeightPercentage,
        soWholeFluidMolePercentage: item.soWholeFluidMolePercentage
      });

      separatorGasList.push({
        separatorGasId: item.recombinationRecombinedSeparatorId,
        componentId: item.componentId,
        componentName,
        component,
        sgWeightPercentage: item.sgWeightPercentage,
        sgMolePercentage: item.sgMolePercentage
      });
    }

    return { recombinedOilList, separatorOilList, separatorGasList };
  }

  ProcessRecombinedOilExcelData(
    data: unknown[],
    componentMaster: ComponentMaster[],
    existingData: RecombinedOilModel[],
    recombinedOilExcelUploadColumns: string[],
    fileInput: FileInputLike
  ): ExcelProcessError<'columns'> | ExcelProcessError<'missingComponents'> & { data: ComponentMaster[] } | ExcelProcessSuccess<RecombinedOilModel[]> {
    const rows = (Array.isArray(data) ? data : []) as ExcelRow[];

    let issue = 0;
    const objectkeys = Object.keys(((rows[0] ?? {}) as Record<string, unknown>));

    recombinedOilExcelUploadColumns.forEach((key) => {
      if (objectkeys.indexOf(key) < 0) {
        issue = 1;
      }
    });

    if (issue !== 0) return { error: 'columns' };

    const result = componentMaster.filter(c1 =>
      !rows.some(c2 => {
        const c2Comp = (c2['Component'] as string | undefined)?.trim().toLowerCase();
        const c1Comp = (c1.component ?? '').trim().toLowerCase();
        return c2Comp === c1Comp;
      })
    );

    if (result.length > 0) return { error: 'missingComponents', data: result } as ExcelProcessError<'missingComponents'> & { data: ComponentMaster[] };

    const convertData = rows.map((res) => ({
      recombinedOilId: 0,
      component: ((res['Component'] as string | undefined) ?? '').trim(),
      componentName: ((res[''] as string | undefined) ?? ''),
      roFlashedLiquidWeightPercentage: this.ParseNumber(res['Flashed Liquid Weight %']),
      roFlashedLiquidMolePercentage: this.ParseNumber(res['Flashed Liquid Mole %']),
      roFlashedGasWeightPercentage: this.ParseNumber(res['Flashed Gas Weight %']),
      roFlashedGasMolePercentage: this.ParseNumber(res['Flashed Gas Mole %']),
      roWholeFluidWeightPercentage: this.ParseNumber(res['Whole Fluid Weight %']),
      roWholeFluidMolePercentage: this.ParseNumber(res['Whole Fluid Mole %'])
    }));

    for (let i = 0; i < existingData.length; i++) {
      const existing = existingData[i];
      const updated = convertData.find(item =>
        (item.component ?? '').trim().toLowerCase() === (existing.component ?? '').trim().toLowerCase()
      );

      if (updated) {
        existingData[i] = {
          ...existing,
          roFlashedLiquidWeightPercentage: updated.roFlashedLiquidWeightPercentage,
          roFlashedLiquidMolePercentage: updated.roFlashedLiquidMolePercentage,
          roFlashedGasWeightPercentage: updated.roFlashedGasWeightPercentage,
          roFlashedGasMolePercentage: updated.roFlashedGasMolePercentage,
          roWholeFluidWeightPercentage: updated.roWholeFluidWeightPercentage,
          roWholeFluidMolePercentage: updated.roWholeFluidMolePercentage
        };
      }
    }

    fileInput.nativeElement.value = '';
    return { data: existingData };
  }

  ProcessSeparatorExcelData(
    data: unknown[],
    existingOil: SeparatorOilModel[],
    existingGas: SeparatorGasModel[],
    oilColumns: string[],
    gasColumns: string[],
    fileInput: FileInputLike
  ):
    | ExcelProcessError<'invalid'>
    | ExcelProcessSuccess<{ type: 'oil'; data: SeparatorOilModel[] }>
    | ExcelProcessSuccess<{ type: 'gas'; data: SeparatorGasModel[] }> {

    const rows = (Array.isArray(data) ? data : []) as ExcelRow[];
    const headers = Object.keys(((rows[0] ?? {}) as Record<string, unknown>));
    const hasColumns = (expected: string[]) => expected.every(h => headers.includes(h));

    if (hasColumns(oilColumns)) {
      const convertData = rows.map((res) => ({
        component: ((res['Component'] as string | undefined) ?? '').trim(),
        soFlashedLiquidWeightPercentage: this.ParseNumber(res['Flashed Liquid Weight %']),
        soFlashedLiquidMolePercentage: this.ParseNumber(res['Flashed Liquid Mole %']),
        soFlashedGasWeightPercentage: this.ParseNumber(res['Flashed Gas Weight %']),
        soFlashedGasMolePercentage: this.ParseNumber(res['Flashed Gas Mole %']),
        soWholeFluidWeightPercentage: this.ParseNumber(res['Whole Fluid Weight %']),
        soWholeFluidMolePercentage: this.ParseNumber(res['Whole Fluid Mole %'])
      }));

      for (let i = 0; i < existingOil.length; i++) {
        const existing = existingOil[i];
        const updated = convertData.find(item =>
          (item.component ?? '').trim().toLowerCase() === (existing.component ?? '').trim().toLowerCase()
        );
        if (updated) {
          existingOil[i] = { ...existing, ...updated };
        }
      }

      fileInput.nativeElement.value = '';
      return { data: { type: 'oil', data: existingOil } };
    }

    if (hasColumns(gasColumns)) {
      const convertData = rows.map((res) => ({
        component: ((res['Component'] as string | undefined) ?? '').trim(),
        sgWeightPercentage: this.ParseNumber(res['Separator Gas Weight %']),
        sgMolePercentage: this.ParseNumber(res['Separator Gas Mole %'])
      }));

      for (let i = 0; i < existingGas.length; i++) {
        const existing = existingGas[i];
        const updated = convertData.find(item =>
          (item.component ?? '').trim().toLowerCase() === (existing.component ?? '').trim().toLowerCase()
        );
        if (updated) {
          existingGas[i] = { ...existing, ...updated };
        }
      }

      fileInput.nativeElement.value = '';
      return { data: { type: 'gas', data: existingGas } };
    }

    fileInput.nativeElement.value = '';
    return { error: 'invalid' };
  }

  HasOilTableErrors(
    data: (RecombinedOilModel | SeparatorOilModel)[],
    table: string,
    componentC2Id: number,
    componentC12Id: number
  ): boolean {
    let fields: string[] = [];

    if (table === RECOMBINATION_CONSTANTS.recombinedOil) {
      fields = [
        RECOMBINATION_CONSTANTS.roFlashedLiquidWeightPercentage,
        RECOMBINATION_CONSTANTS.roFlashedLiquidMolePercentage,
        RECOMBINATION_CONSTANTS.roFlashedGasWeightPercentage,
        RECOMBINATION_CONSTANTS.roFlashedGasMolePercentage,
        RECOMBINATION_CONSTANTS.roWholeFluidWeightPercentage,
        RECOMBINATION_CONSTANTS.roWholeFluidMolePercentage
      ];
    } else {
      fields = [
        RECOMBINATION_CONSTANTS.soFlashedLiquidWeightPercentage,
        RECOMBINATION_CONSTANTS.soFlashedLiquidMolePercentage,
        RECOMBINATION_CONSTANTS.soFlashedGasWeightPercentage,
        RECOMBINATION_CONSTANTS.soFlashedGasMolePercentage,
        RECOMBINATION_CONSTANTS.soWholeFluidWeightPercentage,
        RECOMBINATION_CONSTANTS.soWholeFluidMolePercentage
      ];
    }

    return data.some(item => {
      const componentId = item.componentId ?? null;

      return fields.some(key => {
        const value = item[key as keyof typeof item];

        const isFlashedLiquid = key.includes(RECOMBINATION_CONSTANTS.flashedLiquidNoSpace);
        const isFlashedGas = key.includes(RECOMBINATION_CONSTANTS.flashedGasNoSpace);

        const isExempt =
          (isFlashedLiquid && componentId !== null && componentId <= componentC2Id) ||
          (isFlashedGas && componentId !== null && componentId > componentC12Id);

        if (isExempt) return false;

        return this.IsMissingValue(value) || this.HasInvalidNumberFormat(value);
      });
    });
  }

  HasGasTableErrors(
    data: SeparatorGasModel[],
    componentC12Id: number
  ): boolean {
    const fields = [
      RECOMBINATION_CONSTANTS.sgWeightPercentage,
      RECOMBINATION_CONSTANTS.sgMolePercentage
    ];

    return data.some(item => {
      const componentId = item.componentId ?? null;

      return fields.some(key => {
        const value = item[key as keyof SeparatorGasModel];

        const isRequired = componentId !== null && componentId <= componentC12Id;

        if (isRequired && this.IsMissingValue(value)) return true;
        if (this.IsMissingValue(value)) return false;

        return this.HasInvalidNumberFormat(value);
      });
    });
  }

  ValidateField(value: unknown, label: string, rowIndex: number, fieldKey: string): { isValid: boolean; message: string; fieldId?: string } {
    if (value === null || value === undefined || value === '') {
      return {
        isValid: false,
        message: `Please enter value for ${label} for row number: ${rowIndex + 1}`,
        fieldId: `${fieldKey}_${rowIndex}`
      };
    }

    if (!REGEX_CONSTANTS.NUMBER_WITH_OPTIONAL_DECIMAL.test(String(value))) {
      return {
        isValid: false,
        message: `Please enter valid value for ${label} for row number: ${rowIndex + 1}`,
        fieldId: `${fieldKey}_${rowIndex}`
      };
    }

    return { isValid: true, message: '' };
  }

  CheckDataRange(min: number, max: number, label: string, i: number, value: unknown): { warning: boolean; message: string } {
    if (value !== undefined && value !== null && value !== '' && (Number(value) < min || Number(value) > max)) {
      if (i === 0) {
        return { warning: true, message: `Input value out of range for: ${label}` };
      }
      return { warning: true, message: `Input value out of range: ${label} for row number: ${i}` };
    }
    return { warning: false, message: '' };
  }

  ValidateOilData(
    data: (RecombinedOilModel | SeparatorOilModel)[],
    table: string,
    componentC2Id: number,
    componentC12Id: number,
    tabValidations: boolean
  ): { status: boolean; error: boolean; warning: boolean; messages: string[] } {
    let error = false;
    let warning = false;
    const messages: string[] = [];

    let fields: { key: string; label: string }[] = [];

    if (table === RECOMBINATION_CONSTANTS.recombinedOil) {
      fields = [
        { key: RECOMBINATION_CONSTANTS.roFlashedLiquidWeightPercentage, label: RECOMBINATION_CONSTANTS.flashedLiquidWeightPercent },
        { key: RECOMBINATION_CONSTANTS.roFlashedLiquidMolePercentage, label: RECOMBINATION_CONSTANTS.flashedLiquidMolePercent },
        { key: RECOMBINATION_CONSTANTS.roFlashedGasWeightPercentage, label: RECOMBINATION_CONSTANTS.flashedGasWeightPercent },
        { key: RECOMBINATION_CONSTANTS.roFlashedGasMolePercentage, label: RECOMBINATION_CONSTANTS.flashedGasMolePercent },
        { key: RECOMBINATION_CONSTANTS.roWholeFluidWeightPercentage, label: RECOMBINATION_CONSTANTS.wholeFluidWeightPercent },
        { key: RECOMBINATION_CONSTANTS.roWholeFluidMolePercentage, label: RECOMBINATION_CONSTANTS.wholeFluidMolePercent }
      ];
    } else {
      fields = [
        { key: RECOMBINATION_CONSTANTS.soFlashedLiquidWeightPercentage, label: RECOMBINATION_CONSTANTS.flashedLiquidWeightPercent },
        { key: RECOMBINATION_CONSTANTS.soFlashedLiquidMolePercentage, label: RECOMBINATION_CONSTANTS.flashedLiquidMolePercent },
        { key: RECOMBINATION_CONSTANTS.soFlashedGasWeightPercentage, label: RECOMBINATION_CONSTANTS.flashedGasWeightPercent },
        { key: RECOMBINATION_CONSTANTS.soFlashedGasMolePercentage, label: RECOMBINATION_CONSTANTS.flashedGasMolePercent },
        { key: RECOMBINATION_CONSTANTS.soWholeFluidWeightPercentage, label: RECOMBINATION_CONSTANTS.wholeFluidWeightPercent },
        { key: RECOMBINATION_CONSTANTS.soWholeFluidMolePercentage, label: RECOMBINATION_CONSTANTS.wholeFluidMolePercent }
      ];
    }

    for (let i = 0; i < data.length; i++) {
      const row = data[i];

      for (const field of fields) {
        const value = row[field.key as keyof typeof row];
        const componentId = row.componentId ?? null;

        const isFlashedLiquid = field.label.includes(RECOMBINATION_CONSTANTS.flashedLiquidNoSpace);
        const isFlashedGas = field.label.includes(RECOMBINATION_CONSTANTS.flashedGasNoSpace);

        const isExempt =
          (isFlashedLiquid && componentId !== null && componentId <= componentC2Id) ||
          (isFlashedGas && componentId !== null && componentId > componentC12Id);

        if (!isExempt) {
          const result = this.ValidateField(value, field.label, i, field.key);
          if (!result.isValid) {
            error = true;
            messages.push(result.message);
          }
        }

        if (tabValidations) {
          const range = this.CheckDataRange(0, 100, field.label, i + 1, value);
          if (range.warning) {
            warning = true;
            messages.push(range.message);
          }
        }
      }
    }

    return { status: !error, error, warning, messages };
  }

  ValidateGasData(
    data: SeparatorGasModel[],
    componentC12Id: number,
    tabValidations: boolean
  ): { status: boolean; error: boolean; warning: boolean; messages: string[] } {
    let error = false;
    let warning = false;
    const messages: string[] = [];

    const fields = [
      { key: RECOMBINATION_CONSTANTS.sgWeightPercentage, label: RECOMBINATION_CONSTANTS.separatorGasWeightPercent },
      { key: RECOMBINATION_CONSTANTS.sgMolePercentage, label: RECOMBINATION_CONSTANTS.separatorGasMolePercent }
    ];

    for (let i = 0; i < data.length; i++) {
      const row = data[i];
      const componentId = row.componentId ?? null;

      for (const field of fields) {
        const value = row[field.key as keyof SeparatorGasModel] as unknown;

        if (
          componentId !== null &&
          componentId <= componentC12Id &&
          (value === null || value === undefined || value === '')
        ) {
          const result = this.ValidateField(value, field.label, i, field.key);
          if (!result.isValid) {
            error = true;
            messages.push(result.message);
          }
        }

        if (tabValidations) {
          const range = this.CheckDataRange(0, 100, field.label, i + 1, value);
          if (range.warning) {
            warning = true;
            messages.push(range.message);
          }
        }
      }
    }

    return { status: !error, error, warning, messages };
  }

  ValidateInputForm(form: FormLike, tabValidations: boolean): { status: boolean; error: boolean; warning: boolean; messages: string[] } {
    let error = false;
    let warning = false;
    const messages: string[] = [];

    const fields: { control: string; label: string; range: [number, number] }[] = [
      { control: RECOMBINATION_CONSTANTS.targetSepGOR, label: RECOMBINATION_CONSTANTS.targetSepGORTwo, range: [0, 100000] },
      { control: RECOMBINATION_CONSTANTS.sepOilDensity, label: RECOMBINATION_CONSTANTS.sepOilDensityTwo, range: [0, 2] },
      { control: RECOMBINATION_CONSTANTS.sepOilMW, label: RECOMBINATION_CONSTANTS.SepOilMWTwo, range: [100, 500] },
      { control: RECOMBINATION_CONSTANTS.recombOilCThirtySixPlusMW, label: RECOMBINATION_CONSTANTS.recombOilCThirtySixPlusMWTwo, range: [500, 1200] },
      { control: RECOMBINATION_CONSTANTS.recombFlashOilDensity, label: RECOMBINATION_CONSTANTS.recombFlashOilDensityTwo, range: [0, 2] },
      { control: RECOMBINATION_CONSTANTS.recombMeasGOR, label: RECOMBINATION_CONSTANTS.recombMeasGORTwo, range: [0, 100000] }
    ];

    for (const field of fields) {
      const value = form.controls[field.control].value;

      if (value === null || value === undefined || value === '') {
        error = true;
        messages.push(`Please enter value for ${field.label}`);
      }

      if (tabValidations) {
        const range = this.CheckDataRange(field.range[0], field.range[1], field.label, 0, value);
        if (range.warning) {
          warning = true;
          messages.push(range.message);
        }
      }
    }

    return { status: !error, error, warning, messages };
  }

  ValidateFormField(form: FormLike, controlName: string, label: string): { isValid: boolean; message: string } {
    const value = form.controls[controlName]?.value;
    if (value === '' || value === null || value === undefined) {
      return { isValid: false, message: `Please enter value for ${label}` };
    }
    return { isValid: true, message: '' };
  }

  ValidateFormRange(
    form: FormLike,
    controlName: string,
    label: string,
    min: number,
    max: number,
    tabValidations: boolean
  ): { warning: boolean; message: string } {
    const value = form.controls[controlName]?.value;
    if (!tabValidations || value === null || value === undefined || value === PC.emptyString) {
      return { warning: false, message: '' };
    }
    return this.CheckDataRange(min, max, label, NUMBERS_CONSTANTS.zero, value);
  }

  ApplyRequiredValidators(form: FormLike, controlNames: string[]): void {
    for (let item = NUMBERS_CONSTANTS.zero; item < controlNames.length; item++) {
      const control = form.controls[controlNames[item]] as {
        setValidators?: (validators: unknown) => void;
        updateValueAndValidity?: () => void;
      } | undefined;
      if (!control) {
        continue;
      }
      control.setValidators?.([Validators.required]);
      control.updateValueAndValidity?.();
    }
  }

  CheckValue(compound: string | undefined): boolean {
    const repeatedCompounds: string[] = [
      COMPONENT_CONSTANTS.C6,
      COMPONENT_CONSTANTS.C7,
      COMPONENT_CONSTANTS.C8,
      COMPONENT_CONSTANTS.C9
    ];

    if (compound === undefined || repeatedCompounds.indexOf(compound) === NUMBERS_CONSTANTS.n1Neg) {
      return false;
    }
    return true;
  }

  MapResultData(recombinationResult: RecombinationResult) {
    return {
      compositionalCheckData: recombinationResult?.compositionalQC ?? [],
      wellstreamData: recombinationResult?.wellStreamCalculation ?? [],
      compositionalQCGORCheckData: recombinationResult?.compositionalQCGORCheck ?? null,
      recombinationPropertiesData: recombinationResult?.recombinationProperties ?? null
    };
  }

  GetNextTabIndex(tabGroup: MatTabGroup) {
    if (!tabGroup || !(tabGroup instanceof MatTabGroup)) return null;
    const tabCount = tabGroup._tabs.length;
    const selectedIndex = tabGroup.selectedIndex || NUMBERS_CONSTANTS.zero;
    return (selectedIndex + NUMBERS_CONSTANTS.one) % tabCount;
  }

  GetPreviousTabIndex(tabGroup: MatTabGroup) {
    if (!tabGroup || !(tabGroup instanceof MatTabGroup)) return null;
    const tabCount = tabGroup._tabs.length;
    const selectedIndex = tabGroup.selectedIndex || NUMBERS_CONSTANTS.zero;
    if (selectedIndex !== NUMBERS_CONSTANTS.zero) {
      return (selectedIndex - NUMBERS_CONSTANTS.one) % tabCount;
    }
    return selectedIndex;
  }

}